/**
 * React Query hooks for the Campaigns (fundraising) API.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { campaignsApi } from "@/api/campaigns";
import { getApiErrorMessage } from "@/utils/formatters";
import { LIST_QUERY_OPTIONS } from "@/hooks/queryOptions";
import type {
  ListCampaignsParams,
  ListContributionsParams,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  SetCampaignStatusRequest,
  CampaignItemInput,
  UpdateCampaignItemRequest,
} from "@/types/campaign";

/**
 * Query keys for Campaigns.
 */
export const campaignsKeys = {
  all: ["campaigns"] as const,
  lists: () => [...campaignsKeys.all, "list"] as const,
  list: (params?: ListCampaignsParams) =>
    [...campaignsKeys.lists(), params] as const,
  details: () => [...campaignsKeys.all, "detail"] as const,
  detail: (id: string) => [...campaignsKeys.details(), id] as const,
  contributions: (campaignId: string, params?: ListContributionsParams) =>
    [...campaignsKeys.all, "contributions", campaignId, params] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const useCampaigns = (params?: ListCampaignsParams) =>
  useQuery({
    ...LIST_QUERY_OPTIONS,
    queryKey: campaignsKeys.list(params),
    queryFn: async () => (await campaignsApi.list(params)).data,
  });

/**
 * Fundraising roll-up for the admin dashboard. Only fetched for managers —
 * gate the calling component by role so non-managers never trigger a 403.
 */
export const useCampaignSummary = (enabled = true) =>
  useQuery({
    queryKey: [...campaignsKeys.all, "summary"] as const,
    queryFn: async () => (await campaignsApi.summary()).data,
    enabled,
    staleTime: 60_000,
  });

export const useCampaign = (id: string) =>
  useQuery({
    queryKey: campaignsKeys.detail(id),
    queryFn: async () => (await campaignsApi.get(id)).data,
    enabled: !!id,
  });

export const useCampaignContributions = (
  campaignId: string,
  params?: ListContributionsParams,
) =>
  useQuery({
    ...LIST_QUERY_OPTIONS,
    queryKey: campaignsKeys.contributions(campaignId, params),
    queryFn: async () =>
      (await campaignsApi.listContributions(campaignId, params)).data,
    enabled: !!campaignId,
  });

// ---------------------------------------------------------------------------
// Campaign mutations
// ---------------------------------------------------------------------------

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateCampaignRequest) =>
      (await campaignsApi.create(data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
      toast.success("Campaign created");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to create campaign")),
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateCampaignRequest;
    }) => (await campaignsApi.update(id, data)).data,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: campaignsKeys.detail(variables.id),
      });
      toast.success("Campaign updated");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to update campaign")),
  });
};

export const useSetCampaignStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: SetCampaignStatusRequest;
    }) => (await campaignsApi.setStatus(id, data)).data,
    onSuccess: (campaign, variables) => {
      queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: campaignsKeys.detail(variables.id),
      });
      const messages: Record<string, string> = {
        active: "Campaign is live",
        paused: "Campaign paused",
        ended: "Campaign ended",
      };
      toast.success(messages[campaign.status] ?? "Campaign updated");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to change campaign status")),
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await campaignsApi.remove(id)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignsKeys.lists() });
      toast.success("Campaign deleted");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to delete campaign")),
  });
};

// ---------------------------------------------------------------------------
// Item mutations (all invalidate the parent campaign detail)
// ---------------------------------------------------------------------------

export const useAddCampaignItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      campaignId,
      data,
    }: {
      campaignId: string;
      data: CampaignItemInput;
    }) => (await campaignsApi.addItem(campaignId, data)).data,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: campaignsKeys.detail(variables.campaignId),
      });
      toast.success("Item added");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to add item")),
  });
};

export const useUpdateCampaignItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      campaignId,
      itemId,
      data,
    }: {
      campaignId: string;
      itemId: string;
      data: UpdateCampaignItemRequest;
    }) => (await campaignsApi.updateItem(campaignId, itemId, data)).data,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: campaignsKeys.detail(variables.campaignId),
      });
      toast.success("Item updated");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to update item")),
  });
};

export const useDeleteCampaignItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      campaignId,
      itemId,
    }: {
      campaignId: string;
      itemId: string;
    }) => (await campaignsApi.deleteItem(campaignId, itemId)).data,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: campaignsKeys.detail(variables.campaignId),
      });
      toast.success("Item deleted");
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to delete item")),
  });
};

// ---------------------------------------------------------------------------
// Sponsor CSV export (blob → browser download)
// ---------------------------------------------------------------------------

export const useExportContributions = () =>
  useMutation({
    mutationFn: async (campaignId: string) => {
      const response = await campaignsApi.exportContributions(campaignId);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "campaign-sponsors.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    onError: (error: unknown) =>
      toast.error(getApiErrorMessage(error, "Failed to export sponsors")),
  });
